import { FC, memo } from 'react';
import Image from 'next/image';
import { FaCheck } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';

import { SafeType } from '@benzinga/safe-await';
import { Authentication } from '@benzinga/session';
import { Register } from '../../Register';
import { AuthIterationType } from '../types';
import { buildCheckoutUrl, getPaywallConfig, handlePaywallLoginRedirect } from '../../../utils/paywallUtils';
import { usePaywallTracking } from '../../../hooks/usePaywallTracking';

import '../paywall.scss';
import { EdgeLogoSvg } from '../SophiIteration';
import bearImg from '../SophiIteration/assets/poly-bear.png';
import bullImg from '../SophiIteration/assets/poly-bull.png';
import SocialButton from '../SocialButton';

interface OptimizelyRegPaywallProps extends AuthIterationType {
  isMobile?: boolean;
  paywallId?: string;
  onRegister?: (auth: SafeType<Authentication>, authFor: string) => void;
  onSocialClicked?: (socialAuth: string, forceUrl?: string) => void;
}

export const OptimizelyRegPaywall: FC<OptimizelyRegPaywallProps> = memo(
  ({ allowClose, isMobile, onRegister, onSocialClicked, placement, setShowPaywall }) => {
    const adType = allowClose ? 'soft' : 'hard';
    const adScreen = isMobile ? 'mo' : 'de';
    const adId = `${adType}-${adScreen}-optimizely-reg`;
    const paywallId = 'optimizely-reg';
    const { hasAltCheckout, utmSource } = getPaywallConfig(paywallId, {}, 'optimizely-reg', 'optimizely-reg');

    const campaign = 'command-center-optimizely-reg';

    const checkoutUrl = buildCheckoutUrl({
      ad: adId,
      adType: 'regwall',
      campaign,
      hasAltCheckout,
    });

    const listItems = [
      'Unmatched, global, round-the-clock news',
      'Hand-picked insights that are easy to find and use',
      "Our Daily Briefing newsletter, delivering the day's headline right to your inbox",
    ];

    usePaywallTracking({
      allowClose,
      checkoutUrl,
      isRegwall: true,
      placement,
      utmSource,
    });

    const handleLoginRedirect = () => handlePaywallLoginRedirect(utmSource);
    const handleRegistrationRedirect = () => handlePaywallLoginRedirect(utmSource, 'register');
    const nextRedirect = encodeURIComponent(window.location.href);

    return (
      <>
        <div className="fixed inset-0 z-[1000002] touch-none pointer-events-none"></div>
        <div
          className={
            'paywall-content w-full max-h-[100vh] h-auto animate-fade-up animate-delay-300 animate-once no-scrollbar overflow-scroll flex flex-col md:flex-row items-center md:items-start justify-start md:justify-center md:gap-12 fixed z-[1000003] bottom-0 left-0 bg-white ' +
            'paywall-wrapper'
          }
        >
          {allowClose && setShowPaywall && (
            <button
              className="absolute top-2 right-4 text-black z-10"
              onClick={() => {
                setShowPaywall(false);
              }}
            >
              <FiX />
            </button>
          )}
          <div
            className={
              'optimizely-reg-paywall min-h-[285px] w-full flex flex-col items-center overflow-hidden bg-blue-950'
            }
          >
            <div className={'paywall-logo-wrapper'}>
              <EdgeLogoSvg fill="white" />
              <span>exclusive</span>
            </div>
            <div className="content-wrapper flex-grow">
              <div className="paywall-text-section flex flex-col justify-center items-start">
                <span className="paywall-subtitle">You have reached your free article limit</span>
                <div className="paywall-title">Register for FREE to continue using Benzinga.com</div>
                <div className="paywall-list-wrapper">
                  <div className="paywall-list-heading">Free registration gives you access to:</div>
                  <ul className="paywall-list">
                    {listItems.map((item, index) => (
                      <li className="paywall-list-item" key={index}>
                        <FaCheck className="paywall-check-icon" size={16} />
                        <div>{item}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="paywall-auth-buttons">
                <div className="flex gap-4 paywall-social-buttons">
                  <SocialButton nextUrl={nextRedirect} onSocialClicked={onSocialClicked} socialType="google" />
                  <SocialButton nextUrl={nextRedirect} onSocialClicked={onSocialClicked} socialType="apple" />
                  <SocialButton nextUrl={nextRedirect} onSocialClicked={onSocialClicked} socialType="microsoft" />
                </div>
                <div className="flex-1 text-center my-2 relative paywall-or-continue-with">
                  <div className="divider-line w-full h-[1px] bg-blue-400 absolute top-1/2 left-0"></div>
                  <span className="text-blue-400 relative bg-blue-950 px-4 text-sm">or continue with</span>
                </div>
                <div className="flex gap-4 paywall-login-buttons">
                  <button
                    className="flex-1 p-2 bg-blue-700 text-center text-white hover:bg-blue-600 font-bold lg:p-3 cursor-pointer paywall-login-button"
                    onClick={handleLoginRedirect}
                  >
                    LOGIN
                  </button>
                  <button
                    className="flex-1 p-2 bg-blue-700 text-center text-white hover:bg-blue-600 font-bold lg:p-3 paywall-register-button"
                    onClick={handleRegistrationRedirect}
                  >
                    SIGN UP
                  </button>
                </div>
              </div>
              <div className="paywall-register-form">
                <Register isPaywalled={true} onRegister={onRegister} />
                <button
                  className="text-gray-300 text-xs text-center paywall-login-redirect"
                  onClick={handleLoginRedirect}
                >
                  Already a member? <u>Log in</u>
                </button>
              </div>
            </div>
            <div className="absolute bottom-0 w-full flex flex-row justify-between">
              <Image alt="poly bear" className={'desktop-animal'} height={798} src={bearImg} width={524}></Image>
              <Image alt="poly bull" className={'desktop-animal'} height={798} src={bullImg} width={524}></Image>
            </div>
          </div>
        </div>
      </>
    );
  },
);

OptimizelyRegPaywall.displayName = 'OptimizelyRegPaywall';
